//=============================================================================
//                       <--- Developed By Icon Team --->
//=============================================================================

import React, { useState, useContext } from "react";
import * as moment from "moment";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

// custom pages
import { styles } from "./styles";
import KpiReport from "apis/Report/Report";
import Context from "context/context";

// material-ui
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";


//=============================================================================
// First report year
//=============================================================================

const FIRST_REPORT_YEAR = 2024;


//=============================================================================
// Available years
//=============================================================================

function getAvailableYears() {
    const currentYear = new Date().getFullYear();

    const years = [];

    for (
        let year = currentYear;
        year >= FIRST_REPORT_YEAR;
        year--
    ) {
        years.push(year);
    }

    return years;
}


//=============================================================================
// Component
//=============================================================================

export default function SignatureRetentionReport() {

    const { state, dispatch } = useContext(Context);

    const { SignatureRetentionAPI } = KpiReport();

    const classes = styles();

    const availableYears = getAvailableYears();


    //=========================================================================
    // State
    //=========================================================================

    // No default year
    const [selectedYear, setSelectedYear] = useState(null);

    const [anchorEl, setAnchorEl] = useState(null);

    const [loading, setLoading] = useState(false);


    //=========================================================================
    // Select Year
    //=========================================================================

    const handleYearSelect = (year) => {

        setSelectedYear(year);

        setAnchorEl(null);
    };


    //=========================================================================
    // Export Excel
    //=========================================================================

    const handleExport = async () => {

        //=====================================================================
        // Validate year
        //=====================================================================

        if (!selectedYear) {

            toast.error(
                "Please select a year before exporting the report.",
                {
                    toastId:
                        "signature-retention-year-required",
                }
            );

            return;
        }


        setLoading(true);


        try {

            //=================================================================
            // Backend payload
            //=================================================================

            const payload = {
                year: Number(selectedYear),
            };


            console.log(
                "Signature Retention Report Request:",
                payload
            );


            //=================================================================
            // API call
            //=================================================================

            const response =
                await SignatureRetentionAPI(payload);


            console.log(
                "Signature Retention Report Response:",
                response
            );


            //=================================================================
            // Handle error
            //=================================================================

            if (
                response?.type === "ERROR" ||
                response?.success === false
            ) {

                toast.error(
                    response?.message ||
                    "Unable to export the report.",
                    {
                        toastId:
                            "signature-retention-export-error",
                    }
                );

                return;
            }


            //=================================================================
            // Get report data
            //=================================================================

            const report =
                response?.data;


            if (
                !report ||
                !report.months ||
                report.months.length === 0
            ) {

                toast.info(
                    "No data found for the selected year.",
                    {
                        toastId:
                            "signature-retention-no-data",
                    }
                );

                return;
            }


            //=================================================================
            // Excel headers
            //=================================================================

            const headers = [
                "Month",
                "Starting Subscribers",
                "New Subscribers",
                "Cancellations",
                "Gross Subscriber Change",
                "Ending Subscribers",
                "Net Subscriber Growth",
                "Net Subscriber Growth (%)",
                "Churn (Total Lost Members)",
                "Monthly Churn Rate (%)",
                "Monthly Retention Rate (%)",
            ];


            //=================================================================
            // Excel rows
            //=================================================================

            const rows = report.months.map((month) => {

                return [
                    month.month
                        ? moment(month.month).format("MMM-YY")
                        : "",

                    month.startingSubscribers ?? "",

                    month.newSubscribers ?? "",

                    month.cancellations ?? "",

                    month.grossSubscriberChange ?? "",

                    month.endingSubscribers ?? "",

                    month.netSubscriberGrowth ?? "",

                    month.netSubscriberGrowthPct ?? null,

                    month.churn ?? "",

                    month.monthlyChurnRatePct ?? null,

                    month.monthlyRetentionRatePct ?? null,
                ];
            });


            //=================================================================
            // Complete Excel data
            //=================================================================

            const excelData = [
                headers,
                ...rows,
            ];


            //=================================================================
            // Yearly retention
            //=================================================================

            if (
                report.isYearComplete &&
                report.yearlyRetentionRatePct !== null &&
                report.yearlyRetentionRatePct !== undefined
            ) {

                excelData.push([]);

                excelData.push([
                    "Yearly Retention Rate (%)",
                    report.yearlyRetentionRatePct,
                ]);
            }


            //=================================================================
            // Create worksheet
            //=================================================================

            const worksheet =
                XLSX.utils.aoa_to_sheet(
                    excelData
                );


            //=================================================================
            // Column widths
            //=================================================================

            worksheet["!cols"] = [
                { wch: 14 },
                { wch: 22 },
                { wch: 20 },
                { wch: 18 },
                { wch: 25 },
                { wch: 20 },
                { wch: 24 },
                { wch: 27 },
                { wch: 30 },
                { wch: 25 },
                { wch: 30 },
            ];


            //=================================================================
            // Percentage columns
            //
            // Excel columns:
            //
            // H = Net Subscriber Growth (%)
            // J = Monthly Churn Rate (%)
            // K = Monthly Retention Rate (%)
            //=================================================================

            report.months.forEach(
                (month, index) => {

                    const excelRow =
                        index + 2;


                    const percentageColumns = [
                        "H",
                        "J",
                        "K",
                    ];


                    percentageColumns.forEach(
                        (column) => {

                            const cell =
                                worksheet[
                                    `${column}${excelRow}`
                                ];


                            if (
                                cell &&
                                cell.v !== null &&
                                cell.v !== undefined
                            ) {

                                cell.z = "0.00%";
                            }
                        }
                    );
                }
            );


            //=================================================================
            // Yearly retention formatting
            //=================================================================

            if (
                report.isYearComplete &&
                report.yearlyRetentionRatePct !== null &&
                report.yearlyRetentionRatePct !== undefined
            ) {

                const yearlyRow =
                    excelData.length;


                const yearlyCell =
                    worksheet[
                        `B${yearlyRow}`
                    ];


                if (yearlyCell) {

                    yearlyCell.z = "0.00%";
                }
            }


            //=================================================================
            // Descriptions sheet - explains each column's formula/meaning
            //=================================================================

            const descriptionRows = [
                ["Column", "Formula / Meaning"],
                ["Month", "Reporting month"],
                ["Starting Subscribers", "Active subscribers on day 1 of the month"],
                ["New Subscribers", "New members enrolled during the month"],
                ["Cancellations", "Members who cancelled during the month"],
                ["Gross Subscriber Change", "New Subscribers - Cancellations"],
                ["Ending Subscribers", "Starting Subscribers + New Subscribers - Cancellations"],
                ["Net Subscriber Growth", "Start -> End change (same value as Gross Subscriber Change)"],
                ["Net Subscriber Growth (%)", "Net Subscriber Growth divided by Starting Subscribers"],
                ["Churn (Total Lost Members)", "Members lost / cancelled during the month"],
                ["Monthly Churn Rate (%)", "Cancellations divided by Starting Subscribers"],
                ["Monthly Retention Rate (%)", "Share of Starting Subscribers retained through the month"],
                [
                    "Yearly Retention Rate (%)",
                    "Share of January's Starting Subscribers still retained through December. " +
                    "Only shown once the selected year is complete.",
                ],
            ];

            const descriptionSheet = XLSX.utils.aoa_to_sheet(descriptionRows);

            descriptionSheet["!cols"] = [
                { wch: 28 },
                { wch: 70 },
            ];


            //=================================================================
            // Create workbook
            //=================================================================

            const workbook =
                XLSX.utils.book_new();


            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Thrive Signature Net Subscriber"
            );

            XLSX.utils.book_append_sheet(
                workbook,
                descriptionSheet,
                "Descriptions"
            );


            //=================================================================
            // File name
            //=================================================================

            const fileName =
                `Signature Retention Report ${selectedYear} ${moment(
                    new Date()
                ).format(
                    "MMM DD YYYY"
                )}.xlsx`;


            //=================================================================
            // Download Excel
            //=================================================================

            XLSX.writeFile(
                workbook,
                fileName
            );


            //=================================================================
            // Success toast
            //=================================================================

            toast.success(
                `Signature retention report downloaded for ${selectedYear}.`,
                {
                    toastId:
                        "signature-retention-export-success",
                }
            );

        } catch (error) {

            console.error(
                "Signature Retention Export Error:",
                error
            );

            toast.error(
                "Unable to download the report right now.",
                {
                    toastId:
                        "signature-retention-export-error",
                }
            );

        } finally {

            setLoading(false);
        }
    };


    //=========================================================================
    // UI
    //=========================================================================

    return (
        <div>

            <div
                className={classes.exportAndPreview}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 16,
                    marginTop: 20,
                    marginBottom: 20,
                }}
            >

                {/*=============================================================
                    Select Year - pill: blue label + centered white value
                =============================================================*/}

                <div
                    onClick={(event) =>
                        setAnchorEl(
                            event.currentTarget
                        )
                    }
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        minWidth: 260,
                        height: 46,
                        borderRadius: 23,
                        border: "1px solid #E0E0E0",
                        overflow: "hidden",
                        backgroundColor: "#FFFFFF",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        cursor: "pointer",
                    }}
                >

                    {/* Label half - same blue as Export Excel */}
                    <div
                        style={{
                            backgroundColor: "#305698",
                            color: "#FFFFFF",
                            fontWeight: 700,
                            fontSize: 15,
                            letterSpacing: 0.2,
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            padding: "0 22px",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Select Year
                    </div>

                    {/* White value half - centered text, arrow pinned to the right */}
                    <div
                        style={{
                            flex: 1,
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            color: selectedYear ? "#333" : "#999",
                            fontSize: 15,
                        }}
                    >
                        <span>
                            {selectedYear ? selectedYear : ""}
                        </span>
                        <ArrowDropDownIcon
                            style={{
                                position: "absolute",
                                right: 14,
                                color: "#8A8A8A",
                            }}
                        />
                    </div>

                </div>


                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() =>
                        setAnchorEl(null)
                    }
                    getContentAnchorEl={null}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    PaperProps={{
                        style: {
                            marginTop: 4,
                            minWidth: 220,
                        },
                    }}
                >

                    {availableYears.map(
                        (year) => (

                            <MenuItem
                                key={year}
                                selected={
                                    year ===
                                    selectedYear
                                }
                                onClick={() =>
                                    handleYearSelect(
                                        year
                                    )
                                }
                            >
                                {year}
                            </MenuItem>

                        )
                    )}

                </Menu>


                {/*=============================================================
                    Export Excel - stacked below the year selector
                =============================================================*/}

                <Button
                    variant="contained"
                    className={classes.exportBtn}
                    onClick={handleExport}
                    disabled={loading}
                    disableElevation
                    style={{
                        minWidth: 180,
                        height: 46,
                        borderRadius: 8,
                        backgroundColor: "#305698",
                        color: "#FFFFFF",
                        textTransform: "none",
                        fontSize: 15,
                        fontWeight: 500,
                        letterSpacing: 1,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                    }}
                >

                    {loading
                        ? "Exporting..."
                        : "Export Excel"}

                </Button>

            </div>

        </div>
    );
}